import { useCallback, useMemo, useState } from 'react';
import type { ArchiveItem } from '../types';
import type { DecoratedArchiveItem, NoticeState } from './types';
import { ARCHIVED_STORAGE_KEY, DISMISSED_STORAGE_KEY } from './constants';
import {
  addCleanupHistoryEntry,
  removeCleanupHistoryEntry,
} from '../../../../../hooks/useCleanupHistory';

function loadIds(key: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(key);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveIds(key: string, ids: Set<string>): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(Array.from(ids)));
  } catch {}
}

interface UseArchiveActionsResult {
  decoratedItems: DecoratedArchiveItem[];
  pendingCount: number;
  notice: NoticeState | null;
  archive: (id: string) => void;
  dismiss: (id: string) => void;
  undo: () => void;
  clearNotice: () => void;
  archiveAll: () => void;
  dismissAll: () => void;
}

export function useArchiveActions(items: ArchiveItem[]): UseArchiveActionsResult {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => loadIds(DISMISSED_STORAGE_KEY));
  const [archivedIds, setArchivedIds] = useState<Set<string>>(() => loadIds(ARCHIVED_STORAGE_KEY));
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const decoratedItems = useMemo<DecoratedArchiveItem[]>(() => {
    return items
      .filter((item) => !archivedIds.has(item.id) && !dismissedIds.has(item.id))
      .map((item) => ({ ...item, status: 'pending' }));
  }, [items, dismissedIds, archivedIds]);

  const pendingCount = decoratedItems.length;

  const archive = useCallback(
    (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      const historyId = `h-${Date.now()}`;
      addCleanupHistoryEntry({
        id: historyId,
        dateChanged: new Date().toISOString(),
        job: 'archive',
        jobLabel: 'Archive unused',
        jobIcon: 'A',
        approvedBy: 'Claire Kemanzi',
        approvedByVela: false,
        description: `Archived "${item.accountName}"`,
        canUndo: true,
        undoPayload: { kind: 'archive', accountIds: [id] },
      });
      const next = new Set(archivedIds);
      next.add(id);
      setArchivedIds(next);
      saveIds(ARCHIVED_STORAGE_KEY, next);
      setNotice({
        message: `${item.accountName} archived.`,
        action: 'archive',
        itemIds: [id],
        historyIds: [historyId],
      });
    },
    [items, archivedIds]
  );

  const dismiss = useCallback(
    (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      const next = new Set(dismissedIds);
      next.add(id);
      setDismissedIds(next);
      saveIds(DISMISSED_STORAGE_KEY, next);
      setNotice({
        message: `${item.accountName} dismissed.`,
        action: 'dismiss',
        itemIds: [id],
        historyIds: [],
      });
    },
    [items, dismissedIds]
  );

  const undo = useCallback(() => {
    if (!notice) return;
    if (notice.action === 'archive') {
      const next = new Set(archivedIds);
      notice.itemIds.forEach((id) => next.delete(id));
      setArchivedIds(next);
      saveIds(ARCHIVED_STORAGE_KEY, next);
      notice.historyIds.forEach((id) => removeCleanupHistoryEntry(id));
    } else {
      const next = new Set(dismissedIds);
      notice.itemIds.forEach((id) => next.delete(id));
      setDismissedIds(next);
      saveIds(DISMISSED_STORAGE_KEY, next);
    }
    setNotice(null);
  }, [notice, archivedIds, dismissedIds]);

  const clearNotice = useCallback(() => setNotice(null), []);

  const archiveAll = useCallback(() => {
    const pending = items.filter((i) => !archivedIds.has(i.id) && !dismissedIds.has(i.id));
    if (pending.length === 0) return;
    const next = new Set(archivedIds);
    const historyIds: string[] = [];
    pending.forEach((item, idx) => {
      next.add(item.id);
      const hid = `h-${Date.now()}-${idx}`;
      historyIds.push(hid);
      addCleanupHistoryEntry({
        id: hid,
        dateChanged: new Date().toISOString(),
        job: 'archive',
        jobLabel: 'Archive unused',
        jobIcon: 'A',
        approvedBy: 'Claire Kemanzi',
        approvedByVela: false,
        description: `Archived "${item.accountName}"`,
        canUndo: true,
        undoPayload: { kind: 'archive', accountIds: [item.id] },
      });
    });
    setArchivedIds(next);
    saveIds(ARCHIVED_STORAGE_KEY, next);
    setNotice({
      message: `${pending.length} account${pending.length !== 1 ? 's' : ''} archived.`,
      action: 'archive',
      itemIds: pending.map((p) => p.id),
      historyIds,
    });
  }, [items, archivedIds, dismissedIds]);

  const dismissAll = useCallback(() => {
    const pending = items.filter((i) => !archivedIds.has(i.id) && !dismissedIds.has(i.id));
    if (pending.length === 0) return;
    const next = new Set(dismissedIds);
    pending.forEach((item) => next.add(item.id));
    setDismissedIds(next);
    saveIds(DISMISSED_STORAGE_KEY, next);
    setNotice({
      message: `${pending.length} account${pending.length !== 1 ? 's' : ''} dismissed.`,
      action: 'dismiss',
      itemIds: pending.map((p) => p.id),
      historyIds: [],
    });
  }, [items, archivedIds, dismissedIds]);

  return {
    decoratedItems,
    pendingCount,
    notice,
    archive,
    dismiss,
    undo,
    clearNotice,
    archiveAll,
    dismissAll,
  };
}